/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { createApi } from "@reduxjs/toolkit/query/react"

import { serializeQueryArgsExcludingUiOptions } from "queries/serializeQueryArgs"
import { HttpMethod } from "queries/types"
import {
  MexcoreapipbListProductsResponse,
  MexcoreapipbGetItemsByProductIDsResponse,
  MexcoreapipbListCategoryTreeNodesResponse,
  MexcoreapipbListCategoriesResponse,
  MexcoreapipbListProductsByCategoryIDResponse,
  MexcoreapipbProductSpu,
  MexcoreapipbProductSpuAttributeValue,
  MexcoreapipbEcomItemDetail,
  MexcoreapipbEcomItemAttributeValue,
  MexcoreapipbUpdateProductItemsResponse,
  MexcoreapipbCreateProductItemsResponse,
  MexcoreapipbCreateCategoryRequest,
  MexcoreapipbCreateCategoryResponse,
  MexcoreapipbUpdateCategoryRequest,
  MexcoreapipbUpdateCategoryProductsRequest,
  MexcoreapipbUpdateCategoryProductsResponse,
  MexcoreapipbBatchUpdateCategoriesRequest,
  MexcoreapipbBatchUpdateCategoriesResponse,
  MexcoreapipbGetProductByIDResponse,
  MexcoreapipbUpdateProductItemsStatusByIDRequest,
  MexcoreapipbUpdateItemRequest,
  MexcoreapipbSearchEcomProductSpusResponse,
} from "generated/mexcoreapi-sellercenter.type"
import { BusinessType } from "types/models/menu"
import {
  ECOMMERCE_MAIN_MERCHANT_ID_KEY,
  PAGINATION,
  STOREFRONT_LIMITS,
} from "../constants"
import { ATTRIBUTE_STATUS, DEFAULT_STATUS } from "../constants/constants"
import {
  sellerCenterApiReducerPath,
  sellerCenterEndpoints,
  SELLER_CENTER_CACHE_TAGS,
} from "./constants"
import { sellerCenterBaseQuery } from "./sellerCenterBaseQuery"

// Request/Response types for endpoint params and responses

interface ListProductsParams {
  merchantID: string
  limit?: number
  seek?: string
  sortBy?: string
  asc?: boolean
}

interface GetItemsByProductIDParams {
  productSpuID: string
  merchantID: string
}

interface ListCategoryTreeNodesParams {
  countryID: number
  level?: number
  parentID?: string
  categoryTreeNodeIDs?: string[]
  name?: string
  loadAttributeClusters?: boolean
  locale?: string
}

interface ListCategoriesParams {
  merchantID: string
  limit?: number
  offset?: number
  status?: string[]
  orderBy?: string
  asc?: boolean
}

interface ListProductsByCategoryIDParams {
  categoryID: string
  merchantID: string
  limit?: number
  seek?: string
}

interface GetProductParams {
  productSpuID: string
  showGlobalLoading?: boolean
}

interface UpdateCategoryParams extends MexcoreapipbUpdateCategoryRequest {
  showGlobalLoading?: boolean
}

type EcomItemDetailForCreate = Partial<
  Omit<MexcoreapipbEcomItemDetail, "ecomItemAttributeValues">
> & {
  ecomItemAttributeValues: Partial<MexcoreapipbEcomItemAttributeValue>[]
}

// Create product request type — omits server-managed timestamps and the
// top-level productSpuID (only used for update, not for create).
export interface CreateProductRequest {
  merchantID: string
  categoryTreeNodeID: string
  productSpu: Partial<MexcoreapipbProductSpu>
  productSpuAttributeValues: Partial<MexcoreapipbProductSpuAttributeValue>[]
  ecomItemAndAttributes: EcomItemDetailForCreate[]
  videoID?: string
}

// Update shares the same payload shape as create, with productSpuID added for the path.
export type UpdateProductRequest = CreateProductRequest & {
  productSpuID: string
}

export interface GetVideoUploadUrlParams {
  merchantID: string
  enableThumbnail?: boolean
}

export interface GetVideoUploadUrlResponse {
  presignedURL: string
  videoID: string
}

export interface GetVideoStatusParams {
  merchantID: string
  videoID: string
}

export enum VideoState {
  PROCESSING = "PROCESSING",
  PROCESSED = "PROCESSED",
  REJECTED = "REJECTED",
  FAILED = "FAILED",
}

export interface GetVideoStatusResponse {
  videoID: string
  state: VideoState
  videoCDNLink?: string
  videoS3URL?: string
  thumbnailURL?: string
}

interface UpdateItemParams extends MexcoreapipbUpdateItemRequest {
  productSpuID: string
}

interface UpdateProductStatusParams
  extends MexcoreapipbUpdateProductItemsStatusByIDRequest {
  showGlobalLoading?: boolean
}

export interface SearchProductsParams {
  merchantID: string
  query: string
  statuses?: string[]
  sortBy?: string
  asc?: boolean
  limit?: number
  cursor?: string
  availableAt?: string
}

export interface DownloadBulkUploadTemplateRequest {
  uploadType: string
  merchantIDs: string[]
}

export interface DownloadBulkUploadTemplateResponse {
  presignedURL: string
}

// Maps an FE merchantID string to the array shape the search API expects.
// Empty string and the "main" (All Stores) sentinel both mean "no filter" → [].
export const toSearchMerchantIDArray = (merchantID: string): string[] =>
  merchantID && merchantID !== ECOMMERCE_MAIN_MERCHANT_ID_KEY
    ? [merchantID]
    : []

// API Slice

export const sellerCenterApi = createApi({
  reducerPath: sellerCenterApiReducerPath,
  baseQuery: sellerCenterBaseQuery,
  serializeQueryArgs: serializeQueryArgsExcludingUiOptions,
  refetchOnMountOrArgChange: 300,
  tagTypes: [
    SELLER_CENTER_CACHE_TAGS.products,
    SELLER_CENTER_CACHE_TAGS.categories,
    SELLER_CENTER_CACHE_TAGS.items,
  ],
  endpoints: builder => ({
    listProducts: builder.query<
      MexcoreapipbListProductsResponse,
      ListProductsParams
    >({
      query: ({ merchantID, limit, seek, sortBy, asc }) => ({
        url: sellerCenterEndpoints.listProducts,
        params: { merchantID, limit, seek, sortBy, asc },
      }),
      providesTags: [SELLER_CENTER_CACHE_TAGS.products],
    }),

    searchProducts: builder.query<
      MexcoreapipbSearchEcomProductSpusResponse,
      SearchProductsParams
    >({
      query: ({
        merchantID,
        query,
        statuses = [],
        sortBy = "",
        asc = false,
        limit,
        cursor = "",
        availableAt,
      }) => ({
        url: sellerCenterEndpoints.searchProducts,
        method: HttpMethod.POST,
        data: {
          merchantID: toSearchMerchantIDArray(merchantID),
          query,
          statuses,
          categoryIDs: [],
          sortBy,
          asc,
          limit,
          cursor,
          ...(availableAt && { availableAt }),
        },
      }),
      providesTags: [SELLER_CENTER_CACHE_TAGS.products],
    }),

    getItemsByProductID: builder.query<
      MexcoreapipbGetItemsByProductIDsResponse,
      GetItemsByProductIDParams
    >({
      query: ({ productSpuID, merchantID }) => ({
        url: sellerCenterEndpoints.getItemsByProductIDs,
        params: {
          productSpuIDs: [productSpuID],
          merchantID,
          limit: PAGINATION.DEFAULT_LIMIT_VARIANT_ITEMS,
        },
      }),
      transformResponse: (
        response: MexcoreapipbGetItemsByProductIDsResponse,
      ) => ({
        ...response,
        ecomItems: response.ecomItems?.filter(
          item => item.status !== DEFAULT_STATUS.ARCHIVED,
        ),
      }),
      providesTags: (result, error, arg) => [
        { type: SELLER_CENTER_CACHE_TAGS.items, id: arg.productSpuID },
      ],
    }),

    listCategoryTreeNodes: builder.query<
      MexcoreapipbListCategoryTreeNodesResponse,
      ListCategoryTreeNodesParams
    >({
      query: params => ({
        url: sellerCenterEndpoints.listCategoryTreeNodes,
        params: { ...params, businessType: BusinessType.Shop },
      }),
      transformResponse: (
        response: MexcoreapipbListCategoryTreeNodesResponse,
      ) => ({
        ...response,
        categoryTreeNodes: response.categoryTreeNodes
          ?.filter(node => node.status === DEFAULT_STATUS.ACTIVE)
          .map(node => ({
            ...node,
            attributeClusters: node.attributeClusters?.filter(
              cluster =>
                cluster.status === DEFAULT_STATUS.ACTIVE &&
                cluster.attributeCluster.status === ATTRIBUTE_STATUS.ENABLED,
            ),
          })),
      }),
    }),

    getProduct: builder.query<
      MexcoreapipbGetProductByIDResponse,
      GetProductParams
    >({
      query: ({ productSpuID, showGlobalLoading }) => ({
        url: sellerCenterEndpoints.getProduct(productSpuID),
        extraOptions: { showGlobalLoading },
      }),
      transformResponse: (response: MexcoreapipbGetProductByIDResponse) => {
        const transformed = {
          ...response,
          productSpuAttributeValues: response.productSpuAttributeValues?.filter(
            attr => attr.status === ATTRIBUTE_STATUS.ENABLED,
          ),
          ecomItemDetail: response.ecomItemDetail
            ?.filter(item => item.status !== DEFAULT_STATUS.ARCHIVED)
            .map(item => ({
              ...item,
              ecomItemAttributeValues: item.ecomItemAttributeValues?.filter(
                attr => attr.status === ATTRIBUTE_STATUS.ENABLED,
              ),
            })),
        }

        return transformed
      },
      providesTags: (result, error, arg) => [
        { type: SELLER_CENTER_CACHE_TAGS.products, id: arg.productSpuID },
      ],
    }),

    createProduct: builder.mutation<
      MexcoreapipbCreateProductItemsResponse,
      CreateProductRequest
    >({
      query: body => ({
        url: sellerCenterEndpoints.createProduct,
        method: HttpMethod.POST,
        data: body,
        extraOptions: { showGlobalLoading: true },
      }),
      invalidatesTags: [SELLER_CENTER_CACHE_TAGS.products],
    }),

    updateProduct: builder.mutation<
      MexcoreapipbUpdateProductItemsResponse,
      UpdateProductRequest
    >({
      query: ({ productSpuID, ...body }) => ({
        url: sellerCenterEndpoints.getProduct(productSpuID),
        method: HttpMethod.PUT,
        data: body,
        extraOptions: { showGlobalLoading: true },
      }),
      invalidatesTags: (result, error, arg) => [
        SELLER_CENTER_CACHE_TAGS.products,
        { type: SELLER_CENTER_CACHE_TAGS.products, id: arg.productSpuID },
        { type: SELLER_CENTER_CACHE_TAGS.items, id: arg.productSpuID },
      ],
    }),

    updateProductStatus: builder.mutation<void, UpdateProductStatusParams>({
      query: ({ showGlobalLoading = true, ...body }) => ({
        url: sellerCenterEndpoints.updateProductStatus,
        method: HttpMethod.POST,
        data: body,
        extraOptions: { showGlobalLoading },
      }),
      invalidatesTags: (result, error, arg) => [
        SELLER_CENTER_CACHE_TAGS.products,
        { type: SELLER_CENTER_CACHE_TAGS.products, id: arg.productSpuID },
        { type: SELLER_CENTER_CACHE_TAGS.items, id: arg.productSpuID },
      ],
    }),

    updateItem: builder.mutation<void, UpdateItemParams>({
      query: ({ itemID, productSpuID, ...body }) => ({
        url: sellerCenterEndpoints.updateItem(itemID),
        method: HttpMethod.PUT,
        data: body,
      }),
      invalidatesTags: (result, error, arg) => [
        SELLER_CENTER_CACHE_TAGS.products,
        { type: SELLER_CENTER_CACHE_TAGS.products, id: arg.productSpuID },
        { type: SELLER_CENTER_CACHE_TAGS.items, id: arg.productSpuID },
      ],
    }),

    getVideoUploadUrl: builder.mutation<
      GetVideoUploadUrlResponse,
      GetVideoUploadUrlParams
    >({
      query: ({ merchantID, enableThumbnail = true }) => ({
        url: sellerCenterEndpoints.getVideoUploadUrl,
        params: { merchantID, enableThumbnail },
      }),
    }),

    getVideoStatus: builder.query<GetVideoStatusResponse, GetVideoStatusParams>(
      {
        query: ({ merchantID, videoID }) => ({
          url: sellerCenterEndpoints.getVideoStatus,
          params: { merchantID, videoID },
        }),
      },
    ),

    listCategories: builder.query<
      MexcoreapipbListCategoriesResponse,
      ListCategoriesParams
    >({
      query: ({
        merchantID,
        limit = STOREFRONT_LIMITS.DEFAULT_LIMIT_CATEGORIES_API_CALL,
        offset,
        status = ["ACTIVE", "DEACTIVATED"],
        orderBy = "CmplxSortOrder",
        asc = false,
      }) => ({
        url: sellerCenterEndpoints.listCategories,
        params: { merchantID, limit, offset, status, orderBy, asc },
      }),
      providesTags: [SELLER_CENTER_CACHE_TAGS.categories],
    }),

    createCategory: builder.mutation<
      MexcoreapipbCreateCategoryResponse,
      MexcoreapipbCreateCategoryRequest
    >({
      query: body => ({
        url: sellerCenterEndpoints.createCategory,
        method: HttpMethod.POST,
        data: body,
        extraOptions: { showGlobalLoading: true },
      }),
      invalidatesTags: [SELLER_CENTER_CACHE_TAGS.categories],
    }),

    updateCategory: builder.mutation<void, UpdateCategoryParams>({
      query: ({ categoryID, showGlobalLoading, ...body }) => ({
        url: sellerCenterEndpoints.updateCategory(categoryID),
        method: HttpMethod.PUT,
        data: body,
        extraOptions: { showGlobalLoading },
      }),
      invalidatesTags: [SELLER_CENTER_CACHE_TAGS.categories],
    }),

    updateCategoryProducts: builder.mutation<
      MexcoreapipbUpdateCategoryProductsResponse,
      MexcoreapipbUpdateCategoryProductsRequest
    >({
      query: body => ({
        url: sellerCenterEndpoints.updateCategoryProducts(body.categoryID),
        method: HttpMethod.POST,
        data: body,
        extraOptions: { showGlobalLoading: true },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: SELLER_CENTER_CACHE_TAGS.products, id: arg.categoryID },
      ],
    }),

    batchUpdateCategories: builder.mutation<
      MexcoreapipbBatchUpdateCategoriesResponse,
      MexcoreapipbBatchUpdateCategoriesRequest
    >({
      query: body => ({
        url: sellerCenterEndpoints.batchUpdateCategories,
        method: HttpMethod.PUT,
        data: body,
        extraOptions: { showGlobalLoading: true },
      }),
      invalidatesTags: [SELLER_CENTER_CACHE_TAGS.categories],
    }),

    listProductsByCategoryID: builder.query<
      MexcoreapipbListProductsByCategoryIDResponse,
      ListProductsByCategoryIDParams
    >({
      query: ({
        categoryID,
        merchantID,
        limit = STOREFRONT_LIMITS.MAX_PRODUCTS_PER_COLLECTION,
        seek,
      }) => ({
        url: sellerCenterEndpoints.listProductsByCategoryID(categoryID),
        params: { merchantID, limit, ...(seek && { seek }) },
      }),
      providesTags: (result, error, arg) => [
        { type: SELLER_CENTER_CACHE_TAGS.products, id: arg.categoryID },
      ],
    }),

    downloadBulkUploadTemplate: builder.mutation<
      DownloadBulkUploadTemplateResponse,
      DownloadBulkUploadTemplateRequest
    >({
      query: ({ uploadType, merchantIDs }) => ({
        url: sellerCenterEndpoints.downloadBulkUploadTemplate,
        method: HttpMethod.POST,
        data: { uploadType, merchantIDs },
      }),
    }),
  }),
})

export const {
  useListProductsQuery,
  useLazyListProductsQuery,
  useSearchProductsQuery,
  useLazySearchProductsQuery,
  useGetItemsByProductIDQuery,
  useGetProductQuery,
  useListCategoryTreeNodesQuery,
  useLazyListCategoryTreeNodesQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useGetVideoUploadUrlMutation,
  useLazyGetVideoStatusQuery,
  useListCategoriesQuery,
  useListProductsByCategoryIDQuery,
  useLazyListProductsByCategoryIDQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useUpdateCategoryProductsMutation,
  useBatchUpdateCategoriesMutation,
  useUpdateProductStatusMutation,
  useUpdateItemMutation,
  useDownloadBulkUploadTemplateMutation,
} = sellerCenterApi
